import { MantineProvider } from '@mantine/core';
import { GlobalMessages } from '@shared/localization/messages';
import type { CSSProperties } from 'react';
import React, { useEffect } from 'react';

import IconEdit from '@/assets/Icons/IconEdit';
import IconTrash from '@/assets/Icons/IconTrash';
import { Switch } from '@/components/atoms/Switch';
import type { ExportableDataTableColumn } from '@/components/organism/DataTable/model';
import { eExportFormat } from '@/components/organism/DataTable/model';
import { useMessages } from '@/hooks/messages';
import { usePdfHeaderFooterDataGetter } from '@/hooks/usePdfHeaderFooterDataGetter';

import { Button } from '../atoms/Button';
import { SanitizedHtml } from '../atoms/SanitizedHtml';
import { DataTableComponent } from '../organism/DataTable';
import { assumptionsMessageDefinitions } from './messages';
import type {
  AssumptionActionCallback,
  AssumptionExportData,
  AssumptionModel,
  AssumptionValidityChangeActionCallback,
  EstimationItemActionCallback,
} from './model';

interface AssumptionsTableProps {
  estimationId: string;
  estimationItemId?: string;
  assumptions: AssumptionModel[];
  onAssumptionEditClicked: AssumptionActionCallback;
  onAssumptionDeleteClicked: AssumptionActionCallback;
  onAddAssumptionClicked: EstimationItemActionCallback;
  onAssumptionValidityChanged: AssumptionValidityChangeActionCallback;
  showButtons: boolean;
  showEstimationItem: boolean;
  isReadOnly?: boolean;
  searchText: string;
  setSearchText: (text: string) => void;
}

// Add this style to document head to override the dropdown z-index
const addExportDropdownStyle = () => {
  if (!document.getElementById('export-dropdown-style')) {
    const style = document.createElement('style');
    style.id = 'export-dropdown-style';
    style.innerHTML = `
      .dropdown {
        z-index: 99999 !important;
      }
    `;
    document.head.appendChild(style);
  }
};

export const AssumptionsTable: React.FC<AssumptionsTableProps> = ({
  estimationId,
  estimationItemId,
  assumptions = [],
  onAssumptionEditClicked,
  onAssumptionDeleteClicked,
  onAddAssumptionClicked,
  onAssumptionValidityChanged,
  showButtons,
  showEstimationItem = false,
  isReadOnly,
  searchText,
  setSearchText,
}: AssumptionsTableProps) => {
  useEffect(() => {
    addExportDropdownStyle();
  }, []);

  const messages = useMessages(assumptionsMessageDefinitions);
  const globalMessages = useMessages(GlobalMessages);
  const { getPdfHeaderFooterForEstimation } = usePdfHeaderFooterDataGetter(messages.exportTitle());

  // EWM-1075: only advisory-converted assumptions carry a ref, so the column would be permanently
  // blank on manually built estimations. Show it when at least one row has something to show.
  // The DataTable export filters on excludeFromExport and never looks at `hidden`, so both flags
  // are needed - `hidden` alone still emits a blank column into the CSV/Excel/PDF.
  const hasAdvisoryAssumptionRefs = assumptions.some(
    (assumption) => assumption.advisoryAssumptionRef,
  );

  const columns: ExportableDataTableColumn<AssumptionModel>[] = [
    {
      accessor: 'order',
      title: messages.tableHeaderOrder(),
      noWrap: true,
      sortable: false,
      width: 50,
      render: (_, index) => index + 1,
    },
    {
      accessor: 'advisoryAssumptionRef',
      title: messages.tableHeaderAssumptionId(),
      noWrap: true,
      sortable: false,
      width: 120,
      hidden: !hasAdvisoryAssumptionRefs,
      excludeFromExport: !hasAdvisoryAssumptionRefs,
      render: (assumption) => assumption.advisoryAssumptionRef ?? '',
    },
    {
      accessor: 'estimationItemName',
      title: messages.tableHeaderEstimationItem(),
      noWrap: true,
      sortable: false,
      width: 250,
      cellsClassName: 'flex-grow truncated-table-cell',
      hidden: !showEstimationItem,
      render: (assumption) => (
        <div className='line-clamp-2 text-wrap'>
          {assumption.estimationItem.reference}. {assumption.estimationItem.name}
        </div>
      ),
    },
    {
      accessor: 'categoryName',
      title: messages.tableHeaderAssumptionType(),
      noWrap: true,
      sortable: false,
      width: 350,
      cellsClassName: 'flex-grow truncated-table-cell',
      render: (assumption) => (
        <div className='line-clamp-2 text-wrap'>{assumption.categoryName}</div>
      ),
      footer:
        estimationItemId && !isReadOnly && onAddAssumptionClicked ? (
          <Button
            variant='primary'
            size='sm'
            outlined
            onClick={() => onAddAssumptionClicked(estimationItemId)}
            data-testid='add-assumption-button'
          >
            {messages.addButton()}
          </Button>
        ) : undefined,
    },
    {
      accessor: 'assumption',
      title: messages.tableHeaderAssumption(),
      noWrap: true,
      sortable: false,
      resizable: true,
      cellsClassName: 'flex-grow truncated-table-cell',
      isHtml: true,
      render: (assumption) => (
        <div className='text-wrap' style={{ maxWidth: '300px' }}>
          <SanitizedHtml htmlString={assumption.description} />
        </div>
      ), // TODO: use css text-overflow:ellipse
    },
    {
      accessor: 'isValid',
      title: messages.tableHeaderIsValid(),
      noWrap: true,
      sortable: false,
      width: 100,
      hidden: isReadOnly,
      render: (assumption) => (
        <Switch
          checked={assumption.isValid}
          onChange={(checked) => onAssumptionValidityChanged(assumption.id, checked)}
        />
      ),
    },
    {
      accessor: 'buttons',
      title: '',
      noWrap: true,
      width: 100,
      cellsClassName: '!pl-2 !pr-2',
      excludeFromExport: true,
      render: (assumption) => (
        <div className='flex justify-end gap-2'>
          <button onClick={() => onAssumptionEditClicked(assumption.id)}>
            <IconEdit />
          </button>
          <button onClick={() => onAssumptionDeleteClicked(assumption.id)}>
            <IconTrash />
          </button>
        </div>
      ),
      hidden: isReadOnly || !showButtons,
    },
  ];

  const getExportData = (): Promise<AssumptionExportData[]> => {
    return new Promise((resolve) => {
      const exportData: AssumptionExportData[] = assumptions.map((ass, index) => ({
        order: (index + 1).toString(),
        advisoryAssumptionRef: ass.advisoryAssumptionRef ?? '',
        estimationItemName: `${ass.estimationItem.reference} ${ass.estimationItem.name}`,
        categoryName: ass.categoryName,
        assumption: ass.description,
        isValid: ass.isValid ? globalMessages.yes() : globalMessages.no(),
      }));

      resolve(exportData);
    });
  };

  return (
    <MantineProvider>
      <div data-testid='assumptions-table'>
        <DataTableComponent<AssumptionModel>
          columnsConfiguration={columns}
          hidePagination
          pagination={{
            totalItems: assumptions.length,
            recordsPerPage: 10000, //TODO remove pagination completely
          }}
          data={assumptions}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getExportData={getExportData as any}
          exportConfiguration={[
            eExportFormat.CSV,
            eExportFormat.PDF,
            eExportFormat.PRINT,
            eExportFormat.EXCEL,
          ]}
          exportsAsDropdown
          searchFunction={setSearchText}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          pdfHeaderFooterDataPromise={getPdfHeaderFooterForEstimation(estimationId) as any}
          rowStyle={(record) =>
            !record.estimationItem.name.toLowerCase().includes(searchText.toLowerCase()) &&
            !record.categoryName?.toLowerCase().includes(searchText.toLowerCase()) &&
            !record.description.toLowerCase().includes(searchText.toLowerCase())
              ? ({ visibility: 'collapse' as const } as CSSProperties)
              : ({} as CSSProperties)
          }
        />
      </div>
    </MantineProvider>
  );
};
